import { PipelineStepInstance, PipelineStepType } from './pipelineBuilder.types'
import {
    ApplicationHandbookScope,
    LeadsStepConfig,
    ProspectsStepConfig,
    StepConfigByType,
} from './stepConfig.types'

export function defaultProspectsStepConfig(): ProspectsStepConfig {
    return {
        auto_enroll_email_campaign: false,
        auto_enroll_phone_campaign: false,
    }
}

export function defaultLeadsStepConfig(): LeadsStepConfig {
    return {
        on_enter_automations: {
            send_account_invite: { enabled: true },
        },
        application_handbook: {
            scope: 'workbook_complete',
        },
        enter_behavior: 'auto_start',
    }
}

function resolveApplicationHandbookScope(
    rawConfig: Record<string, unknown>,
): ApplicationHandbookScope {
    const handbook = rawConfig.application_handbook
    if (
        handbook &&
        typeof handbook === 'object' &&
        (handbook as { scope?: string }).scope === 'identity_complete'
    ) {
        return 'identity_complete'
    }

    const portal = rawConfig.portal_collection
    if (portal && typeof portal === 'object') {
        const qualifyingWorksheet = (portal as {
            qualifying_worksheet?: boolean
        }).qualifying_worksheet
        if (qualifyingWorksheet === false) {
            return 'identity_complete'
        }
        if (qualifyingWorksheet === true) {
            return 'workbook_complete'
        }
    }

    return 'workbook_complete'
}

export function defaultStepConfig(type: 'prospects'): ProspectsStepConfig
export function defaultStepConfig(type: 'leads'): LeadsStepConfig
export function defaultStepConfig(
    type: PipelineStepType,
): ProspectsStepConfig | LeadsStepConfig | undefined
export function defaultStepConfig(
    type: PipelineStepType,
): ProspectsStepConfig | LeadsStepConfig | undefined {
    if (type === 'prospects') {
        return defaultProspectsStepConfig()
    }
    if (type === 'leads') {
        return defaultLeadsStepConfig()
    }
    return undefined
}

export function getStepConfig<T extends keyof StepConfigByType>(
    step: PipelineStepInstance,
    type: T,
): StepConfigByType[T] {
    const defaults =
        type === 'prospects'
            ? defaultProspectsStepConfig()
            : defaultLeadsStepConfig()
    if (!step.config || typeof step.config !== 'object') {
        if (type === 'leads' && step.enter_behavior) {
            return {
                ...defaults,
                enter_behavior: step.enter_behavior,
            } as StepConfigByType[T]
        }
        return defaults as StepConfigByType[T]
    }
    const rawConfig = step.config as Record<string, unknown>
    if (type === 'prospects') {
        const legacyAutoEnroll = rawConfig.auto_enroll_campaigns === true
        return {
            auto_enroll_email_campaign:
                typeof rawConfig.auto_enroll_email_campaign === 'boolean'
                    ? rawConfig.auto_enroll_email_campaign
                    : legacyAutoEnroll,
            auto_enroll_phone_campaign:
                typeof rawConfig.auto_enroll_phone_campaign === 'boolean'
                    ? rawConfig.auto_enroll_phone_campaign
                    : false,
        } as StepConfigByType[T]
    }

    const leadsDefaults = defaults as LeadsStepConfig
    const merged = { ...leadsDefaults, ...step.config } as LeadsStepConfig
    const handbookScope = resolveApplicationHandbookScope(rawConfig)

    const {
        portal_collection: _legacyPortal,
        ...mergedWithoutLegacy
    } = merged as LeadsStepConfig & { portal_collection?: unknown }

    const nextConfig: LeadsStepConfig = {
        ...mergedWithoutLegacy,
        application_handbook: { scope: handbookScope },
        on_enter_automations: {
            send_account_invite: {
                enabled:
                    merged.on_enter_automations?.send_account_invite?.enabled ??
                    true,
                ...(merged.on_enter_automations?.send_account_invite
                    ?.template_id
                    ? {
                          template_id:
                              merged.on_enter_automations.send_account_invite
                                  .template_id,
                      }
                    : {}),
            },
        },
    }

    if (
        step.enter_behavior &&
        !(step.config as LeadsStepConfig).enter_behavior
    ) {
        nextConfig.enter_behavior = step.enter_behavior
    }

    return nextConfig as StepConfigByType[T]
}

export function patchStepConfig<T extends Record<string, unknown>>(
    step: PipelineStepInstance,
    partial: Partial<T>,
): PipelineStepInstance {
    const current =
        step.config && typeof step.config === 'object' ? step.config : {}
    return {
        ...step,
        config: { ...current, ...partial },
    }
}
